'use client';

import React from 'react';
import Image from 'next/image';

import { Button } from '@/components/ui/button';
import type { KmpDeactivationModalProps } from '@/presentation/stores/ModalStore';

interface KmpDeactivationModalComponentProps extends KmpDeactivationModalProps {
  onClose?: () => void;
  modalId?: string;
}

const BASE_INFO_ITEMS = [
  'Your total may go up if the product price increases',
  'You may need to pay more to complete your plan',
  'You can turn it back on anytime',
] as const;

const FREE_LOCK_MESSAGE = "You'll lose the FREE price lock period";

export const KmpDeactivationModal: React.FC<KmpDeactivationModalComponentProps> = ({
  currentPrice,
  currency,
  onKeepLocked,
  onTurnOff,
  onClose,
  freeLockPeriodDays,
}) => {
  // Build info items list - add free lock message if there's a free period
  const infoItems = React.useMemo(() => {
    const items: string[] = [...BASE_INFO_ITEMS];
    if (freeLockPeriodDays && freeLockPeriodDays > 0) {
      // Insert the free lock message as the second item for emphasis
      items.splice(1, 0, FREE_LOCK_MESSAGE);
    }
    return items;
  }, [freeLockPeriodDays]);

  const handleKeepLocked = () => {
    onKeepLocked();
    onClose?.();
  };

  const handleTurnOff = () => {
    onTurnOff();
    onClose?.();
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-4 flex flex-col items-center">
        <div className="mb-3">
          <Image
            src="/assets/svgs/kmp/info-orange.svg"
            alt=""
            width={40}
            height={40}
            className="h-10 w-10"
            aria-hidden="true"
          />
        </div>
        <h2 className="text-center text-xl font-bold text-gray-900">
          Are you sure you want to deactivate price lock?
        </h2>
      </div>

      {/* Info list */}
      <div className="mb-4">
        <p className="mb-2 text-sm text-gray-700">Here&apos;s what to know:</p>
        <ol className="space-y-2" aria-label="Information about turning off price lock">
          {infoItems.map((item, index) => (
            <li key={item} className="flex items-start gap-3 text-sm text-gray-700">
              <span
                className="flex size-5 shrink-0 items-center justify-center rounded-full bg-gray-200 text-xs font-medium text-gray-700"
                aria-hidden="true"
              >
                {index + 1}
              </span>
              <span>{item}</span>
            </li>
          ))}
        </ol>
      </div>

      {/* Price info box */}
      <div className="mb-6 rounded-lg bg-motitoBlack p-4 text-white">
        <div className="mb-1 flex items-center gap-2">
          <Image
            src="/assets/svgs/kmp/lightbulb.svg"
            alt=""
            width={16}
            height={16}
            className="h-4 w-4"
            aria-hidden="true"
          />
          <span className="text-sm font-medium">
            Today&apos;s price: {currency} {currentPrice}
          </span>
        </div>
        <p className="text-xs text-gray-300">
          This price is only protected when price lock is ON.
        </p>
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-3">
        <Button
          onClick={handleKeepLocked}
          variant="primary"
          size="lg"
          className="w-full"
        >
          Keep price locked
        </Button>
        <Button
          onClick={handleTurnOff}
          variant="outline"
          size="lg"
          className="w-full"
        >
          Turn price lock off
        </Button>
      </div>
    </div>
  );
};

KmpDeactivationModal.displayName = 'KmpDeactivationModal';

export default KmpDeactivationModal;
